import React from 'react';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { Info } from 'phosphor-react';

/** Ícone ⓘ com tooltip explicativo (mesmo padrão do conciliacao-contabil). */
export const InfoTip = ({ label, size = 12 }: { label: string; size?: number }) => (
  <Tooltip
    label={label}
    fontSize="11.5px"
    hasArrow
    placement="top"
    bg="#0F172A"
    color="#F1F5F9"
    borderRadius="lg"
    px={3}
    py={2}
    maxW="240px"
    lineHeight="1.4"
    openDelay={150}
  >
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      color="#CBD5E1"
      cursor="help"
      _hover={{ color: '#7C3AED' }}
      transition="color 0.15s"
      onClick={e => e.stopPropagation()}
    >
      <Info size={size} weight="bold" />
    </Box>
  </Tooltip>
);

/** Mini-gráfico de tendência (SVG puro, sem lib). */
export const Sparkline = ({ data, color }: { data: number[]; color: string }) => {
  const pts = data.length >= 2 ? data : [0, 0];
  const max = Math.max(...pts);
  const min = Math.min(...pts);
  const range = max - min || 1;
  const w = 72;
  const h = 26;
  const step = w / (pts.length - 1);
  const coords = pts.map(
    (v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`,
  );
  const [lastX, lastY] = coords[coords.length - 1].split(',');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: 'visible' }} aria-hidden>
      <polyline
        points={coords.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r={2.2} fill={color} />
    </svg>
  );
};

/** Cardiograma multi-linha (escala compartilhada) — várias fontes sobrepostas. */
export const MultiSparkline = ({
  series,
  w = 70,
  h = 24,
}: {
  series: { data: number[]; color: string }[];
  w?: number;
  h?: number;
}) => {
  const clean = series.map(s => ({
    ...s,
    data: s.data.map(v => (Number.isFinite(v) ? v : 0)),
  }));
  const all = clean.flatMap(s => s.data);
  const max = all.length ? Math.max(...all) : 0;
  const min = all.length ? Math.min(...all) : 0;
  const range = max - min || 1;
  const len = Math.max(2, ...clean.map(s => s.data.length));
  const step = w / (len - 1);
  const y = (v: number) => (h - ((v - min) / range) * h).toFixed(1);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: 'visible' }} aria-hidden>
      {clean.map(s => {
        if (s.data.length < 2) return null;
        const pts = s.data.map((v, i) => `${(i * step).toFixed(1)},${y(v)}`);
        const [lx, ly] = pts[pts.length - 1].split(',');
        return (
          <g key={s.color}>
            <polyline
              points={pts.join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0.95}
            />
            <circle cx={lx} cy={ly} r={1.9} fill={s.color} />
          </g>
        );
      })}
    </svg>
  );
};

/** Variação do último ponto vs o anterior (com dado). */
export function computeDelta(serie: number[]): number | null {
  let last = -1;
  for (let i = serie.length - 1; i >= 0; i -= 1) {
    if (serie[i] > 0) {
      last = i;
      break;
    }
  }
  if (last <= 0) return null;
  return serie[last] - serie[last - 1];
}

/** Cor da variação: verde na direção "boa", vermelho na ruim. */
export function deltaColorFor(delta: number | null, positiveWhenDown?: boolean): string {
  if (delta == null || delta === 0) return '#94A3B8';
  const good = positiveWhenDown ? delta < 0 : delta > 0;
  return good ? '#10B981' : '#EF4444';
}

// ---------------------------------------------------------------------
// Trilha de triagem — chips que particionam o total e filtram a lista.
// ---------------------------------------------------------------------
export interface ITriagemChip {
  k: string;
  lab: string;
  cor: string;
  valor: number;
  dica: string;
  /** anel vazado no lugar da bolinha cheia (o chip "Todas" e o "Sem dados"). */
  vazado?: boolean;
}

/**
 * Chip da trilha. Zero NÃO some da tela: fica desabilitado. "Divergência · 0" é
 * o resultado mais importante que a tela produz (é como o supervisor sabe que
 * pode fechar o mês) — esconder faria a trilha mudar de posição a cada mês e
 * quebraria a memória muscular de quem usa isso todo dia.
 */
export const TriagemChip = ({
  c,
  ativo,
  onClick,
}: {
  c: ITriagemChip;
  ativo: boolean;
  onClick: () => void;
}) => {
  const zero = c.valor === 0 && !ativo;
  return (
    <Tooltip
      label={c.dica}
      fontSize="11px"
      hasArrow
      placement="top"
      bg="#0F172A"
      color="#F1F5F9"
      borderRadius="lg"
      px={3}
      py={2}
      maxW="240px"
      openDelay={400}
    >
      <Box
        as="button"
        type="button"
        onClick={zero ? undefined : onClick}
        aria-pressed={ativo}
        aria-disabled={zero}
        cursor={zero ? 'default' : 'pointer'}
        opacity={zero ? 0.5 : 1}
        textAlign="left"
        px="11px"
        py="7px"
        borderRadius="11px"
        border="1.5px solid"
        borderColor={ativo ? c.cor : 'rgba(15,23,42,0.08)'}
        bg={ativo ? `${c.cor}18` : 'rgba(255,255,255,0.55)'}
        boxShadow={ativo ? `0 0 0 3px ${c.cor}14` : 'none'}
        _hover={
          zero ? {} : { borderColor: `${c.cor}55`, bg: `${c.cor}12`, transform: 'translateY(-1px)' }
        }
        _focusVisible={{ outline: `2px solid ${c.cor}`, outlineOffset: '2px' }}
        transition="all 0.14s"
        minW="98px"
        flexShrink={0}
      >
        <Flex align="center" gap="6px">
          <Box
            w="7px"
            h="7px"
            borderRadius="full"
            bg={c.vazado ? 'transparent' : c.cor}
            border={c.vazado ? `1.5px solid ${c.cor}` : 'none'}
            flexShrink={0}
          />
          <Text
            fontSize="17px"
            fontWeight={800}
            color={zero ? '#94A3B8' : c.cor}
            lineHeight="1.1"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {c.valor.toLocaleString('pt-BR')}
          </Text>
        </Flex>
        <Text
          fontSize="10.5px"
          fontWeight={ativo ? 700 : 600}
          color={zero ? '#94A3B8' : '#64748b'}
          mt="1px"
          noOfLines={1}
        >
          {c.lab}
        </Text>
      </Box>
    </Tooltip>
  );
};
